import { apiClient } from './apiClient';
import { logError } from '../utils/errorLogger';
import { User } from '../types/event';

export interface HostUser {
  id: string;
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  is_active: boolean;
  is_mirsal_member?: boolean;
  get_full_name: string;
}

export interface UsersAPIResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export const usersAPI = {
  // Get all hosts for admin to assign to events
  getHosts: async (search?: string): Promise<HostUser[]> => {
    try {
      // Build URL with query parameters manually to ensure search is included
      let url = '/users/hosts/';
      const searchTerm = search?.trim();
      if (searchTerm) {
        const params = new URLSearchParams({ search: searchTerm });
        url = `${url}?${params.toString()}`;
      }
      
      // Always disable cache to ensure fresh results
      const response = await apiClient.get<UsersAPIResponse<HostUser[]>>(
        url,
        { cache: false } // Always disable cache for fresh results
      );
      
      const hosts = response.data.data || response.data;
      return hosts;
    } catch (error: any) {
      // Error fetching hosts
      throw error;
    }
  },

  // Get user profile by ID (for validation)
  getUserById: async (userId: string): Promise<User> => {
    try {
      const response = await apiClient.get<UsersAPIResponse<User>>(
        `/users/${userId}/`,
        { cache: true, ttl: 2 * 60 * 1000 } // Cache for 2 minutes
      );
      
      return response.data.data || response.data;
    } catch (error: any) {
      logError('UsersAPI - Error fetching user by ID', error);
      throw error;
    }
  },

  // Update Mirsal member status for a host
  updateMirsalMemberStatus: async (hostId: string, isMirsalMember: boolean): Promise<UsersAPIResponse<{ host_id: string; host_name: string; is_mirsal_member: boolean }>> => {
    try {
      const response = await apiClient.post<UsersAPIResponse<{ host_id: string; host_name: string; is_mirsal_member: boolean }>>(
        '/users/hosts/update-mirsal-member/',
        {
          host_id: hostId,
          is_mirsal_member: isMirsalMember
        }
      );
      return response.data;
    } catch (error: any) {
      logError('UsersAPI - Error updating Mirsal member status', error);
      throw error;
    }
  }
};

export default usersAPI;
